/**
 * BubbleToolbar - Floating toolbar that appears when text is selected
 *
 * This toolbar shows formatting options when the user selects text in the editor.
 * It provides:
 *
 * 1. Text formatting: Bold, Italic, Underline, Strikethrough, Code, Highlight
 * 2. Link editing: Opens LinkDialog to add/edit hyperlinks
 * 3. Comments: Add inline comments to selected text
 *
 * Implementation Notes:
 * - Uses useEffect to track selection changes via editor events
 * - Uses Floating UI for positioning (via @floating-ui/dom which is already installed)
 * - Shows/hides based on selection state
 * - Does NOT use @tiptap/extension-bubble-menu which doesn't have a React component export
 *
 * Usage:
 * ```tsx
 * <BubbleToolbar editor={editor} boardId="board-123" />
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import { LinkDialog } from './LinkDialog';
import { AIBubbleMenu, type AITransformAction } from './AIBubbleMenu';

// ============================================================================
// TYPES
// ============================================================================

interface BubbleToolbarProps {
  /** Tiptap editor instance */
  editor: Editor;
  /** Board ID for creating comments */
  boardId?: string;
  /** WebSocket ref for sending AI requests to Board DO */
  wsRef?: React.RefObject<WebSocket | null>;
  /** Y.XmlFragment field name for AI requests (defaults to 'richtext') */
  fieldName?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function BubbleToolbar({ editor, boardId, wsRef, fieldName = 'richtext' }: BubbleToolbarProps) {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  // Controls toolbar visibility
  const [isVisible, setIsVisible] = useState(false);

  // Controls LinkDialog visibility
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  // Controls adding comment state
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Controls AI dropdown menu visibility
  const [isAIMenuOpen, setIsAIMenuOpen] = useState(false);

  // Ref to the toolbar element for positioning
  const toolbarRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // Listen to editor selection changes and position the toolbar
  useEffect(() => {
    const updateToolbar = () => {
      // Get selection state
      const { selection } = editor.state;
      const { empty } = selection;

      // Don't show if selection is empty (just a cursor, no text selected)
      if (empty) {
        setIsVisible(false);
        return;
      }

      // Don't show when selecting images - they have their own controls
      if (editor.isActive('image')) {
        setIsVisible(false);
        return;
      }

      // Don't show when in code blocks - formatting doesn't apply there
      if (editor.isActive('codeBlock')) {
        setIsVisible(false);
        return;
      }

      // Show the toolbar
      setIsVisible(true);

      // Position the toolbar near the selection
      const { view } = editor;
      const { from, to } = selection;

      // Get DOM range from selection
      const start = view.coordsAtPos(from);
      const end = view.coordsAtPos(to);

      // Create a virtual element for Floating UI
      // This represents the selected text's bounding box
      const virtualElement = {
        getBoundingClientRect() {
          return {
            width: end.left - start.left,
            height: start.bottom - start.top,
            x: start.left,
            y: start.top,
            top: start.top,
            right: end.left,
            bottom: start.bottom,
            left: start.left,
          };
        },
      };

      // Position the toolbar using Floating UI
      if (toolbarRef.current) {
        computePosition(virtualElement, toolbarRef.current, {
          placement: 'top',
          middleware: [
            offset(10), // 10px offset from selection
            flip(), // Flip to bottom if not enough space on top
            shift({ padding: 8 }), // Keep within viewport
          ],
        }).then(({ x, y }) => {
          if (toolbarRef.current) {
            toolbarRef.current.style.left = `${x}px`;
            toolbarRef.current.style.top = `${y}px`;
          }
        });
      }
    };

    // Listen to selection updates and transactions
    editor.on('selectionUpdate', updateToolbar);
    editor.on('transaction', updateToolbar);

    // Initial update
    updateToolbar();

    // Cleanup listeners on unmount
    return () => {
      editor.off('selectionUpdate', updateToolbar);
      editor.off('transaction', updateToolbar);
    };
  }, [editor]);

  // Close AI menu when clicking outside
  useEffect(() => {
    // Only add listener when menu is open
    if (!isAIMenuOpen) return;

    /**
     * Handle clicks outside the AI menu to close it.
     * Uses a class check to determine if click was inside the menu container.
     */
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click was outside the relative container that holds the AI button
      if (!target.closest('.ai-menu-container')) {
        setIsAIMenuOpen(false);
      }
    };

    // Add listener with slight delay to avoid closing on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isAIMenuOpen]);

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------

  /**
   * Handle AI text transformation.
   *
   * Flow:
   * 1. Get selected text range
   * 2. Delete selected text and insert placeholder
   * 3. Send transform request via WebSocket to Board DO
   * 4. Board DO calls AI Gateway and streams response back
   * 5. Y.Doc updates replace placeholder with AI-generated text
   *
   * The streaming response is handled by the Y.Doc sync - we don't need
   * to manually update the editor here.
   */
  const handleAITransform = useCallback(
    async (action: AITransformAction, selectedText: string) => {
      // Store selection range for later replacement
      const { from, to } = editor.state.selection;

      // Check if WebSocket is available
      if (!wsRef?.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.warn('[BubbleToolbar] WebSocket not connected for AI request');
        // Show inline placeholder to indicate processing
        editor
          .chain()
          .focus()
          .deleteRange({ from, to })
          .insertContent(`[AI ${action}: connecting...]`)
          .run();
        return;
      }

      // Delete selected text - AI will stream replacement via Y.Doc
      editor.chain().focus().deleteRange({ from, to }).run();

      // Send AI request via WebSocket to Board DO
      const aiRequest = {
        type: 'ai_request',
        action,
        targetType: 'document',
        targetField: fieldName, // Y.XmlFragment key for Tiptap content
        context: selectedText,
      };

      wsRef.current.send(JSON.stringify(aiRequest));

      console.log('[BubbleToolbar] AI transform request sent:', action, selectedText.slice(0, 50) + '...');
    },
    [editor, wsRef, fieldName]
  );

  /**
   * Get the current link URL if cursor is on a linked text
   *
   * Used to pre-fill the LinkDialog when editing an existing link.
   * Returns empty string if no link at cursor.
   */
  const getCurrentLinkUrl = (): string => {
    // getAttributes returns the attributes of the current node/mark
    const attrs = editor.getAttributes('link');
    return attrs.href || '';
  };

  /**
   * Open the link dialog
   *
   * Called when user clicks the Link button.
   */
  const handleLinkClick = () => {
    setIsLinkDialogOpen(true);
  };

  /**
   * Handle adding a comment to selected text
   *
   * Flow:
   * 1. Prompt user for comment text
   * 2. Create comment via API
   * 3. Apply comment mark to selected text with returned ID
   *
   * TODO: Replace window.prompt with a proper modal dialog
   */
  const handleAddComment = async () => {
    // Require boardId for comment creation
    if (!boardId) {
      console.warn('[BubbleToolbar] boardId required for comments');
      return;
    }

    // Get comment text from user
    // Note: Using window.prompt for simplicity - can be replaced with modal
    const content = window.prompt('Add a comment:');
    if (!content?.trim()) return;

    setIsAddingComment(true);

    try {
      // Create comment via API
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ boardId, content }),
      });

      if (response.ok) {
        const data = await response.json();
        // Apply comment mark to selected text
        // The mark stores the comment ID, content is in D1
        editor.chain().focus().setComment(data.data.id).run();
      } else {
        console.error('[BubbleToolbar] Failed to create comment:', response.status);
      }
    } catch (error) {
      console.error('[BubbleToolbar] Comment error:', error);
    } finally {
      setIsAddingComment(false);
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* Bubble Toolbar - positioned via Floating UI */}
      <div
        ref={toolbarRef}
        className="bubble-toolbar"
        style={{
          position: 'absolute',
          visibility: isVisible ? 'visible' : 'hidden',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.1s ease',
          zIndex: 50,
        }}
      >
        {/* ============================================================
         * TEXT FORMATTING BUTTONS
         * ============================================================ */}

        {/* Bold button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`bubble-toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Bold (Ctrl+B)"
          aria-label="Bold"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        </button>

        {/* Italic button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`bubble-toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Italic (Ctrl+I)"
          aria-label="Italic"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="4" x2="10" y2="4" />
            <line x1="14" y1="20" x2="5" y2="20" />
            <line x1="15" y1="4" x2="9" y2="20" />
          </svg>
        </button>

        {/* Underline button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`bubble-toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
          title="Underline (Ctrl+U)"
          aria-label="Underline"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
            <line x1="4" y1="21" x2="20" y2="21" />
          </svg>
        </button>

        {/* Strikethrough button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`bubble-toolbar-button ${editor.isActive('strike') ? 'is-active' : ''}`}
          title="Strikethrough"
          aria-label="Strikethrough"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="12" x2="20" y2="12" />
            <path d="M17.5 7.5c0-3-2.5-4-5-4-2.5 0-5 1-5 4s2.5 3 5 3" />
            <path d="M6.5 16.5c0 3 2.5 4 5 4 2.5 0 5-1 5-4s-2.5-3-5-3" />
          </svg>
        </button>

        {/* Code (inline) button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`bubble-toolbar-button ${editor.isActive('code') ? 'is-active' : ''}`}
          title="Code"
          aria-label="Code"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        </button>

        {/* Highlight button */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`bubble-toolbar-button ${editor.isActive('highlight') ? 'is-active' : ''}`}
          title="Highlight"
          aria-label="Highlight"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>

        {/* Divider between formatting and link */}
        <div className="bubble-toolbar-divider" />

        {/* ============================================================
         * LINK BUTTON
         * ============================================================ */}

        {/* Link button - opens dialog */}
        <button
          type="button"
          onClick={handleLinkClick}
          className={`bubble-toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
          title="Add link (Ctrl+K)"
          aria-label="Add link"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>

        {/* Divider before comment button */}
        <div className="bubble-toolbar-divider" />

        {/* ============================================================
         * COMMENT BUTTON
         * ============================================================ */}

        {/* Comment button - adds inline comment to selection */}
        <button
          type="button"
          onClick={handleAddComment}
          disabled={isAddingComment || !boardId}
          className={`bubble-toolbar-button ${editor.isActive('comment') ? 'is-active' : ''}`}
          title="Add comment"
          aria-label="Add comment"
        >
          <svg className="bubble-toolbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>

        {/* Divider before AI button */}
        <div className="bubble-toolbar-divider" />

        {/* ============================================================
         * AI TRANSFORM BUTTON
         * ============================================================ */}

        {/* AI Button with dropdown menu */}
        <div className="relative ai-menu-container">
          <button
            type="button"
            onClick={() => setIsAIMenuOpen(!isAIMenuOpen)}
            className={`bubble-toolbar-button ${isAIMenuOpen ? 'is-active' : ''}`}
            title="AI Transform"
            aria-label="AI Transform"
            aria-expanded={isAIMenuOpen}
            aria-haspopup="menu"
          >
            {/* Sparkles icon - indicates AI/magic functionality */}
            <svg
              className="bubble-toolbar-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
            </svg>
          </button>

          {/* AI Dropdown Menu */}
          <AIBubbleMenu
            editor={editor}
            isOpen={isAIMenuOpen}
            onClose={() => setIsAIMenuOpen(false)}
            onTransform={handleAITransform}
          />
        </div>
      </div>

      {/* Link Dialog - rendered outside BubbleToolbar for proper z-index */}
      <LinkDialog
        editor={editor}
        isOpen={isLinkDialogOpen}
        onClose={() => setIsLinkDialogOpen(false)}
        initialUrl={getCurrentLinkUrl()}
      />
    </>
  );
}
